import { TmapLog, TmapLogBuilder, TmapLogType } from '@tmap-web-lib/tmap-log-client'
import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePageIdContext, useTmapLogContext } from '@/providers'

type ContextValue = {
  pageId: string;
  customs: Record<string, any>;
  setPageId: (id: string) => void;
  tmapLogClient: (logType: TmapLogType) => TmapLogBuilder;
}

export const TmapLogContext = createContext<ContextValue>({
  pageId: '',
  customs: {},
  setPageId: () => {},
  tmapLogClient: (logType: TmapLogType) => TmapLog(logType),
})

type Props = PropsWithChildren<Partial<Pick<ContextValue, 'pageId' | 'customs'>>>

export function TmapLogContextProvider({ children, pageId, customs }: Props) {
  const { pageId: parentPageId, customs: parentCustoms } = useTmapLogContext()
  const pageIdContext = usePageIdContext()

  const [currentPageId, setPageId] = useState(pageId)

  const extraCustomFields = useMemo(() => {
    return pageIdContext.extraObject?.logbox ?? {}
  }, [pageIdContext.extraObject?.logbox])

  const merged = useMemo(() => {
    const mergedPageId = currentPageId || parentPageId || ''
    const mergedCustoms = {
      ...parentCustoms,
      ...extraCustomFields,
      ...customs,
    }
    return {
      pageId: mergedPageId,
      customs: mergedCustoms,
    }
  }, [currentPageId, parentPageId, parentCustoms, extraCustomFields, customs])

  const mergedRef = useRef(merged)

  const tmapLogClient = useCallback((logType: TmapLogType) => {
    const log = TmapLog(logType)
    const { pageId, customs } = mergedRef.current

    if (pageId) {
      log.setPageId(pageId)
    }
    Object.keys(customs).forEach((key) => log.setCustom(key, customs[key]))
    return log
  }, [])

  const contextValue = useMemo(() => {
    return {
      ...merged,
      setPageId,
      tmapLogClient,
    }
  }, [merged, tmapLogClient])

  useEffect(() => {
    mergedRef.current = merged
  }, [merged])

  return (
    <TmapLogContext.Provider value={contextValue}>
      {children}
    </TmapLogContext.Provider>
  )
}
